/*──────────────────────────────────────────────────────────────────────────
 * SessionWarningModal — Admin (web)
 *
 * Shows 5 minutes before the access-token cookie expires.
 * Listens for the global `session:issued` custom event (dispatched on login,
 * initializeAuth, and successful token refresh).
 *
 * Timer math (dynamic — no hardcoded values):
 *   expiresAt        = JWT exp claim (Unix ms) — comes from the server
 *                      always in sync with JWT_ACCESS_EXPIRES_IN in .env
 *   WARNING_LEAD_MS  = 5 min — show warning this long before expiry
 *   → warning fires at (expiresAt - 5 min) from now
 *   → auto-logout fires at expiresAt
 *
 * Example with JWT_ACCESS_EXPIRES_IN=24h:
 *   warning fires after 23h 55min of a fresh session
 *───────────────────────────────────────────────────────────────────────────*/

import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Clock as _Clock, LogOut, RefreshCw, ShieldAlert } from 'lucide-react';
import { useAuthStore } from '../../store/auth';

// ── Timer constants ──────────────────────────────────────────────────────────
// WARNING_LEAD_MS: how early to show the warning before expiry.
// This is the only constant on the client — expiresAt itself always comes
// from the server's JWT exp claim (via the session:issued event detail).
const WARNING_LEAD_MS    = 5 * 60 * 1000; // show warning 5 min before expiry
const WARNING_DURATION_S = 5 * 60;        // countdown length in seconds

// ── Component ────────────────────────────────────────────────────────────────
export function SessionWarningModal() {
    const logout      = useAuthStore(s => s.logout);
    const localLogout = useAuthStore(s => s.localLogout);

    const [showWarning, setShowWarning] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(WARNING_DURATION_S);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const warningTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const expiryTimerRef  = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const countdownRef    = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
    const expiresAtRef    = useRef<number>(0);

    const clearAllTimers = useCallback(() => {
        clearTimeout(warningTimerRef.current);
        clearTimeout(expiryTimerRef.current);
        clearInterval(countdownRef.current);
    }, []);

    const autoExpire = useCallback(() => {
        clearAllTimers();
        setShowWarning(false);
        localLogout();
        window.dispatchEvent(new CustomEvent('session:expired'));
    }, [clearAllTimers, localLogout]);

    const startTimers = useCallback((expiresAt: number) => {
        clearAllTimers();
        setShowWarning(false);
        expiresAtRef.current = expiresAt;

        const now         = Date.now();
        const msToWarning = Math.max(0, (expiresAt - WARNING_LEAD_MS) - now);
        const msToExpiry  = Math.max(0, expiresAt - now);

        if (msToExpiry <= 0) { autoExpire(); return; }

        // ── Show warning after (TTL - 5 min) ────────────────────────────────
        warningTimerRef.current = setTimeout(() => {
            setSecondsLeft(Math.max(0, Math.floor((expiresAtRef.current - Date.now()) / 1000)));
            setShowWarning(true);

            countdownRef.current = setInterval(() => {
                const remaining = Math.floor((expiresAtRef.current - Date.now()) / 1000);
                setSecondsLeft(Math.max(0, remaining));
                if (remaining <= 0) clearInterval(countdownRef.current);
            }, 1000);
        }, msToWarning);

        // ── Auto-logout at expiry ────────────────────────────────────────────
        expiryTimerRef.current = setTimeout(autoExpire, msToExpiry);
    }, [clearAllTimers, autoExpire]);

    // ── Listen for session:issued event ─────────────────────────────────────
    // detail.expiresAt is the JWT exp claim (Unix ms) from the server — no client math.
    useEffect(() => {
        const handler = (e: Event) => {
            const expiresAt: number | undefined = (e as CustomEvent<{ expiresAt?: number }>).detail?.expiresAt;
            if (expiresAt) startTimers(expiresAt);
        };
        window.addEventListener('session:issued', handler);
        return () => { window.removeEventListener('session:issued', handler); clearAllTimers(); };
    }, [startTimers, clearAllTimers]);

    // ── "Continue" → call refresh endpoint, reset timers ────────────────────
    const handleContinue = async () => {
        setIsRefreshing(true);
        try {
            const refreshResp = await axios.post('/api/auth/refresh', {}, {
                withCredentials: true,
                headers: { 'X-Tenant-Slug': localStorage.getItem('tenant-slug') || 'main' }
            });
            const newExpiresAt: number | undefined = refreshResp.data?.expiresAt;
            if (newExpiresAt) {
                window.dispatchEvent(new CustomEvent('session:issued', { detail: { expiresAt: newExpiresAt } }));
            }
            setShowWarning(false);
        } catch {
            autoExpire();
        } finally {
            setIsRefreshing(false);
        }
    };

    // ── "Log Out" button ─────────────────────────────────────────────────────
    const handleLogout = () => {
        clearAllTimers();
        setShowWarning(false);
        logout();
    };

    if (!showWarning) return null;

    // ── Circular countdown SVG ───────────────────────────────────────────────
    const progress      = secondsLeft / WARNING_DURATION_S;
    const radius        = 38;
    const circumference = 2 * Math.PI * radius;
    const dashOffset    = circumference * (1 - progress);
    const arcColor      = progress > 0.4 ? '#f59e0b' : progress > 0.15 ? '#f97316' : '#ef4444';
    const mins          = Math.floor(secondsLeft / 60);
    const secs          = secondsLeft % 60;

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="session-warning-title"
        >
            {/* Card */}
            <div
                className="w-full max-w-sm mx-4 rounded-2xl border shadow-2xl overflow-hidden"
                style={{
                    background: 'var(--color-card, #1c1c2e)',
                    borderColor: 'var(--color-border, rgba(255,255,255,0.1))',
                    animation: 'sessionWarnIn 0.25s cubic-bezier(0.34,1.56,0.64,1) both'
                }}
            >
                {/* Amber top accent bar */}
                <div style={{ height: 3, background: `linear-gradient(90deg, ${arcColor}, transparent)`, transition: 'background 1s' }} />

                <div className="px-8 pt-8 pb-6 text-center">
                    {/* Countdown circle */}
                    <div className="relative mx-auto mb-5 flex items-center justify-center" style={{ width: 100, height: 100 }}>
                        <svg className="absolute inset-0" width="100" height="100" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
                            {/* Track */}
                            <circle cx="50" cy="50" r={radius} fill="none" strokeWidth="5" stroke="rgba(255,255,255,0.08)" />
                            {/* Progress */}
                            <circle
                                cx="50" cy="50" r={radius}
                                fill="none" strokeWidth="5"
                                strokeLinecap="round"
                                stroke={arcColor}
                                strokeDasharray={circumference}
                                strokeDashoffset={dashOffset}
                                style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.6s ease' }}
                            />
                        </svg>
                        {/* Icon + time */}
                        <div className="flex flex-col items-center gap-0.5 z-10">
                            <ShieldAlert className="h-4 w-4" style={{ color: arcColor }} />
                            <span className="text-xl font-bold tabular-nums" style={{ color: 'var(--color-foreground, #fff)' }}>
                                {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
                            </span>
                        </div>
                    </div>

                    {/* Heading */}
                    <h2
                        id="session-warning-title"
                        className="text-lg font-bold mb-2"
                        style={{ color: 'var(--color-foreground, #fff)' }}
                    >
                        Sesión por vencer
                    </h2>
                    <p className="text-sm leading-relaxed mb-7" style={{ color: 'var(--color-muted-foreground, rgba(255,255,255,0.55))' }}>
                        Tu sesión expirará en menos de <strong style={{ color: arcColor }}>5 minutos</strong>.
                        Si no realizas ninguna acción, serás desconectado automáticamente.
                    </p>

                    {/* Actions */}
                    <div className="flex gap-3">
                        {/* Log out */}
                        <button
                            onClick={handleLogout}
                            className="flex-1 h-10 flex items-center justify-center gap-2 rounded-xl text-sm font-medium transition-all"
                            style={{
                                border: '1px solid var(--color-border, rgba(255,255,255,0.12))',
                                background: 'transparent',
                                color: 'var(--color-foreground, #fff)',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                            <LogOut className="h-4 w-4" />
                            Cerrar Sesión
                        </button>

                        {/* Continue = refresh */}
                        <button
                            onClick={handleContinue}
                            disabled={isRefreshing}
                            className="flex-1 h-10 flex items-center justify-center gap-2 rounded-xl text-sm font-semibold transition-all disabled:opacity-60"
                            style={{
                                background: 'var(--color-primary, #6366f1)',
                                color: '#fff',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.filter = 'brightness(1.15)')}
                            onMouseLeave={e => (e.currentTarget.style.filter = '')}
                        >
                            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                            {isRefreshing ? 'Renovando…' : 'Continuar'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Keyframe via inline style tag */}
            <style>{`
                @keyframes sessionWarnIn {
                    from { opacity: 0; transform: scale(0.88) translateY(12px); }
                    to   { opacity: 1; transform: scale(1)    translateY(0);    }
                }
            `}</style>
        </div>
    );
}
